import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { FACTION_SPECS, BOT_SKILL_LABELS, VETERAN_DEVELOPMENT_SKILLS, phaseDurationSeconds, veteranDevelopmentPath } from '@tb/shared';
import type { BotSkill, Faction } from '@tb/shared';

import { FrontFrame, FrontLobby, FrontName, FrontDeployment, FrontLoading, SimRoom, onSimPath, startTraining } from './Front';
import { useNet } from './net';
import { rememberedPassword } from './front/gate';
import { Icon, Notice, Segmented, Tag, Toast } from './ui/kit';
const CommandRoot = lazy(() => import('./command/CommandRoot').then((m) => ({ default: m.CommandRoot })));

// ---------------------------------------------------------------------------
// Shell: public entry, deployment, then the game.
// ---------------------------------------------------------------------------

const NAME_KEY = 'tb.name';

/** Local storage is unavailable in some browsers and private windows. */
function stored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function App() {
  const net = useNet();
  const [name, setName] = useState(() => stored(NAME_KEY) ?? '');
  // Stored commanders can resume immediately; new names are entered beside the lobby actions.
  const [entered, setEntered] = useState(() => stored(NAME_KEY) != null);

  // Keep the lobby light. Start warming the city only after joining, while
  // the server's call-up or region deployment leaves time to prepare it.
  const deploying = !!(net.deployment || net.matchId);
  useEffect(() => {
    if (!deploying) return;
    const timer = setTimeout(() => {
      void import('./render3d/engine/warmUp').then((m) => m.warmUp())
        .catch((error) => console.warn('City preparation will retry on demand.', error));
    }, 100);
    return () => clearTimeout(timer);
  }, [deploying]);

  // Entering gives the socket the name, and it announces it on every
  // (re)connect from then on by itself (net.ts): said here on each open too,
  // every reconnect said hello twice.
  useEffect(() => {
    if (entered) net.hello(name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entered]);
  useEffect(() => {
    if (net.state === 'open' && !entered) net.send({ t: 'listMatches' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entered, net.state]);

  const pendingEntry = useRef<(() => void) | null>(null);
  const [preparing, setPreparing] = useState(false);
  // Announce the chosen name before sending the entry action on this socket.
  useEffect(() => {
    if (!pendingEntry.current || !entered || net.state !== 'open' || !net.playerId) return;
    const action = pendingEntry.current;
    pendingEntry.current = null;
    setPreparing(false);
    action();
  }, [entered, net.state, net.playerId]);

  const toasts = (
    <div className="toasts">
      {net.errors.map((e) => <Toast key={e.id}>{e.message}</Toast>)}
      {net.state === 'closed' && <Toast>Connection lost — reconnecting…</Toast>}
      {net.state === 'superseded' && (
        <Toast tone="ok">
          This match is open in another tab or window.
          <button className="btn sm" onClick={net.reconnect}>Play here instead</button>
        </Toast>
      )}
    </div>
  );

  const enter = () => {
    const n = name.trim() || 'Commander';
    try { localStorage.setItem(NAME_KEY, n); } catch { /* not fatal */ }
    setName(n);
    setEntered(true);
  };

  const changeName = () => {
    try { localStorage.removeItem(NAME_KEY); } catch { /* not fatal */ }
    setEntered(false);
  };

  const prepareEntry = (action: () => void) => {
    if (entered && net.playerId && net.state === 'open') { action(); return; }
    pendingEntry.current = action;
    setPreparing(true);
    enter();
  };

  if (entered && net.matchId && net.view) {
    if (net.view.match.status === 'LOBBY') {
      return <>{toasts}<MatchLobby net={net} /></>;
    }
    // One command interface per match: its feed, read marks, reports seen and
    // training coach belong to that match, and Train again or the war after
    // training starts them fresh.
    return <>{toasts}<Suspense fallback={<FrontLoading state={net.state} name={name} />}><CommandRoot key={net.view.match.id} net={net} /></Suspense></>;
  }
  // Joined, and the match's first view on its way; or out of the last match
  // and not yet told where to: the lobby is not where this commander is going.
  if (entered && (net.matchId || net.leaving)) return <>{toasts}<FrontLoading state={net.state} name={name} /></>;

  if (entered && !net.deployment && onSimPath()) return <>{toasts}<SimRoom net={net} name={name} /></>;
  // The frame (and its responsive picture) survives identity and deployment transitions.
  return <>{toasts}<FrontFrame
    state={net.state}
    name={entered ? name : undefined}
    onChangeName={entered && !net.deployment ? changeName : undefined}
    war={net.deployment?.war ?? net.wars[0]}
    onTrain={net.deployment ? undefined : () => prepareEntry(() => startTraining(net, rememberedPassword() ?? undefined))}
  >
    {onSimPath() && !entered ? <FrontName name={name} setName={setName} onEnter={enter} />
      : entered && net.deployment ? <FrontDeployment net={net} />
      : <FrontLobby net={net} name={name} setName={setName} identified={entered} preparing={preparing} prepareEntry={prepareEntry} />}
  </FrontFrame></>;

}

const SKILLS: { id: BotSkill; label: string; note: string }[] = [
  { id: 'RECRUIT', label: 'Recruit', note: 'Half its rounds go nowhere. One wave, random development, leaves its flanks open.' },
  { id: 'REGULAR', label: 'Regular', note: 'Scouts first, works down your value list, stacks SAMs where you hit it.' },
  { id: 'VETERAN', label: 'Veteran', note: 'Uses every available wave, doglegs around your belt, and develops according to conditions.' },
  { id: 'MASTER', label: 'Master', note: 'A Veteran that fills its belt first and keeps its missile store full of ballistic missiles. Masters strike the weakest city together: a recon swarm and ballistic missiles to see it and break its launchers, then everything at once, and every drone and cruise missile on a city coming apart. It fires a nuclear device at once at the weakest city, and lends launchers under a light attack, or to an ally under fire whenever nothing is coming at it.' },
  { id: 'MASTER_PAIR', label: 'Master (two targets)', note: 'A Master that splits its side\'s fire evenly between two cities, the weakest two the survey shows, and keeps its stores full of cruise missiles and strike drones rather than ballistic missiles. Like the Master, it lends launchers to an ally under fire whenever nothing is coming at it.' },
  { id: 'VETERAN_OFFENSIVE', label: 'Veteran (offensive)', note: 'Veteran behaviour, including All Out, but chooses Balanced whenever it would dig in.' },
  { id: 'VETERAN_NOSHELTER', label: 'veteran-noshelter', note: 'Veteran behaviour with starting shelters replaced by C-RAM and short SAM units; never builds more shelters.' },
  { id: 'VETERAN_INDUSTRY', label: 'Veteran (industry)', note: 'Veteran behaviour, but its strikes go first on enemy factories and refineries it can see above half health, until each has had its rounds.' },
  ...(['RECRUIT', 'REGULAR', 'VETERAN'] as const).map((level) => ({
    id: `${level}_FILL` as BotSkill, label: BOT_SKILL_LABELS[`${level}_FILL`],
    note: `${BOT_SKILL_LABELS[level]} behaviour, but every place its interceptor establishment allows, elastic places included, is filled before it repairs, builds or develops anything else.`,
  })),
  ...VETERAN_DEVELOPMENT_SKILLS.map((id) => ({ id, label: BOT_SKILL_LABELS[id], note: 'Veteran decisions with the development order shown, using normal AP costs.' })),
];

// --- pre-game roster -------------------------------------------------------

function MatchLobby({ net }: { net: ReturnType<typeof useNet> }) {
  const view = net.view!;
  const [skill, setSkill] = useState<BotSkill>('REGULAR');
  const seats = view.match.config.perSide * 2;
  const filled = view.match.players.length;
  const hostName =
    view.match.players.find((p) => p.id === view.match.host)?.name ?? 'The commander who opened it';

  return (
    <div className="centered terminal-room">
      <div className="card housing">
        <h1 className="mono">{view.match.id}</h1>
        <p className="sub" style={{ marginBottom: 16 }}>
          Share this code. {view.match.config.perSide}v{view.match.config.perSide},{' '}
          {phaseDurationSeconds(view.match.config, view.match.phase)}s for this phase.
        </p>

        <div className="section-label">Roster ({filled}/{seats})</div>
        <Roster net={net} />

        {!view.match.youAreHost && (
          <Notice tone="mil">
            <strong>{hostName}</strong> opened this room and starts it when the
            roster is ready. You are in — nothing to do but wait.
          </Notice>
        )}

        {view.match.youAreHost && <div className="section-label">NPC commanders</div>}
        {view.match.youAreHost && <Segmented label="NPC commanders" wide value={skill} onChange={setSkill} style={{ marginBottom: 6 }}
          options={SKILLS.filter((s) => !veteranDevelopmentPath(s.id)).map((s) => ({ value: s.id, label: s.label, title: s.note }))} />}
        {view.match.youAreHost && <label className="row wrap" style={{ marginBottom: 8 }}>
          <span className="sub">Veteran development path</span>
          <select className="grow" style={{ minWidth: 0, maxWidth: '100%' }}
            value={veteranDevelopmentPath(skill) ? skill : ''}
            onChange={(e) => { if (e.target.value) setSkill(e.target.value as BotSkill); }}>
            <option value="">Choose a fixed development path…</option>
            {VETERAN_DEVELOPMENT_SKILLS.map((id) => <option key={id} value={id}>{BOT_SKILL_LABELS[id]}</option>)}
          </select>
        </label>}
        {view.match.youAreHost && (
          <>
            <div className="sub" style={{ fontSize: 'var(--fs-sm)', marginBottom: 8 }}>
              {SKILLS.find((s) => s.id === skill)!.note}
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn sm grow"
                disabled={filled >= seats}
                onClick={() => net.addBot(skill)}
              >
                Add one
              </button>
              <button
                className="btn sm grow"
                disabled={filled >= seats}
                onClick={() => net.fillWithBots(skill)}
              >
                Fill all {seats - filled} seats
              </button>
            </div>
          </>
        )}

        <div className="row" style={{ marginTop: 18, gap: 8 }}>
          <button className="btn grow" onClick={net.leaveMatch}>Leave</button>
          {view.match.youAreHost && (
            <button className="btn primary grow" onClick={() => net.startMatch(skill)}>Start</button>
          )}
        </div>
        <p className="sub" style={{ marginTop: 10, fontSize: 'var(--fs-sm)' }}>
          Every cluster starts three against three, four clusters to a region: any seat still empty when you start goes to a{' '}
          {SKILLS.find((s) => s.id === skill)!.label.toLowerCase()} NPC. NPCs plan from the same fogged
          view you get — they have to fly recon to find anything, same as you.
        </p>
      </div>
    </div>
  );
}

function Roster({ net }: { net: ReturnType<typeof useNet> }) {
  const view = net.view!;
  const you = view.you;
  // The roster travels with the view, so it stays correct for players already
  // inside a match — the lobby listing only covers matches you have not joined.
  const players = view.match.players;
  const side = (f: 'WEST' | 'EAST') => players.filter((p) => p.faction === f);

  return (
    <div className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
      {(['WEST', 'EAST'] as const).map((f) => (
        <div key={f} className="grow">
          <div className={`section-label faction-${f}`} style={{ marginTop: 0 }}>
            {FACTION_SPECS[f].title}
          </div>
          {side(f).length === 0 && <div className="sub">—</div>}
          {side(f).map((p) => (
            <div key={p.id} className="item" style={{ padding: '6px 9px' }}>
              <div className="item-title">
                <span>
                  <strong style={{ fontWeight: 500 }}>{p.name}</strong>
                  {p.id === you.id && <Tag>you</Tag>}
                  {p.bot && <Tag tone="mil">NPC</Tag>}
                  {!p.connected && !p.bot && <Tag tone="bad">offline</Tag>}
                </span>
                {p.bot && (
                  <button className="btn sm ghost icon" aria-label={`Remove ${p.name}`} onClick={() => net.removePlayer(p.id)}><Icon name="close" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
